export default function LCContent() {
  const designElements = [
    { label: "Fabric", detail: "100% linen or a linen-cotton blend — natural fibres that filter light beautifully and drape with effortless weight" },
    { label: "Tone", detail: "Warm white, natural linen, oat, or soft sand — avoid stark white or cool grey tones" },
    { label: "Length", detail: "Floor-to-ceiling always — even in rooms with shorter windows, hang the rod close to the ceiling to elongate the space" },
    { label: "Fullness", detail: "1.5x to 2x the window width for a relaxed, gathered look — avoid curtains that barely cover the window" },
    { label: "Header style", detail: "Eyelet or rod pocket for a relaxed look; pinch pleat for a more tailored, elevated feel" },
    { label: "Lining", detail: "Unlined for a sheer, airy effect; thermal lining for warmth and light control" },
  ];

  const stylingTips = [
    "Always hang the curtain rod as close to the ceiling as possible — this makes the room feel taller and the windows feel larger.",
    "Let the curtains pool slightly on the floor (2–5cm) for a relaxed, editorial look — or hem them to just touch the floor for a cleaner finish.",
    "Choose a tone that is slightly warmer than the wall colour — this adds depth without contrast.",
    "In a beige or neutral room, natural linen or warm white curtains work better than stark white, which can feel cold.",
    "Use a double rod to layer sheer linen panels with a heavier linen drape — this gives you full light control without sacrificing the soft look.",
    "Iron linen curtains lightly while slightly damp — this removes creases without making them look stiff.",
    "Avoid synthetic curtains in a calm neutral room — they reflect light differently and tend to look flat and cheap.",
  ];

  return (
    <article className="max-w-2xl mx-auto px-4 md:px-0 py-16">

      <div className="mb-14">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Introduction</p>
        <p className="text-[#4A3F38] text-base md:text-lg leading-relaxed mb-5" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Linen curtains are one of the most transformative things you can add to a calm room.
        </p>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          They soften harsh light, add natural texture, and bring a sense of warmth and ease that no other window treatment quite replicates.
          In a soft beige or neutral room, the right linen curtains do not just cover the window — they complete the atmosphere.
          This guide covers what to look for, how to hang and style them, and which picks we keep returning to.
        </p>
      </div>

      <div className="mb-14">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Why It Works</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Why Linen Curtains Work Better Than Any Other Fabric
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />

        <div className="relative overflow-hidden rounded-2xl mb-8">
          <img
            src="https://public.readdy.ai/ai/img_res/65efa8141cd0ec4c107ab6205841e9a9.jpg"
            alt="Linen curtain texture and light"
            className="w-full h-[280px] md:h-[360px] object-cover object-top"
          />
        </div>

        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-5" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          Linen has a natural weight and drape that synthetic fabrics cannot replicate. It filters light rather than blocking it —
          creating a soft, diffused glow that makes a room feel warm and alive at any time of day.
        </p>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-5" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          In a beige or neutral room, linen curtains add texture without adding colour. They move gently in a breeze,
          catch the light differently throughout the day, and age beautifully — becoming softer and more relaxed over time.
        </p>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          The key is choosing the right tone and hanging them correctly. A linen curtain hung too low or too narrow
          will make the room feel smaller and the windows feel mean. Hung correctly — high, wide, and with generous fullness —
          linen curtains make even a modest room feel genuinely elegant.
        </p>
      </div>

      <div className="mb-14">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>What to Look For</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Key Things to Consider When Choosing Linen Curtains
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />
        <div className="space-y-4">
          {designElements.map((el, i) => (
            <div key={i} className="flex gap-5 items-start border-b border-[#EDE6DA] pb-4 last:border-0">
              <span className="text-[#B5A898] text-[10px] tracking-[0.3em] uppercase w-24 flex-shrink-0 pt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>{el.label}</span>
              <p className="text-[#6B5E53] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>{el.detail}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-14">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Styling</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          How to Hang and Style Linen Curtains
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />

        <div className="relative overflow-hidden rounded-2xl mb-8">
          <img
            src="https://readdy.ai/api/search-image?query=linen%20curtains%20floor%20to%20ceiling%20warm%20beige%20room%20natural%20light%20soft%20drape%20calm%20living%20room%20editorial%20interior%20photography%20warm%20atmosphere&width=900&height=500&seq=lc_style1&orientation=landscape"
            alt="Linen curtains styled in a calm room"
            className="w-full h-[280px] md:h-[360px] object-cover object-top"
          />
        </div>

        <ul className="space-y-4">
          {stylingTips.map((tip, i) => (
            <li key={i} className="flex gap-4 items-start">
              <span className="text-[#C8BAA8] text-[10px] tracking-[0.3em] mt-1 flex-shrink-0" style={{ fontFamily: "'Inter', sans-serif" }}>0{i + 1}</span>
              <p className="text-[#6B5E53] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>{tip}</p>
            </li>
          ))}
        </ul>
      </div>

    </article>
  );
}
